import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, parseApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Receipt, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTimeFixed, statusVi, statusTone } from "@/lib/pos-utils";

const PAGE_SIZE = 20;
const POLL_MS = 30_000;

const STATUS_FILTERS = [
  { v: "", l: "Mọi trạng thái" },
  { v: "open", l: "Mở" },
  { v: "paid", l: "Đã thu" },
  { v: "voided", l: "Đã hủy" },
  { v: "refunded", l: "Đã hoàn" },
  { v: "cancelled", l: "Đã hủy (cancel)" },
];

export function RecentOrders({ channel, channelLabel, refreshToken, onOpenDetail, branches }) {
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const buildUrl = useCallback((append) => {
    const params = new URLSearchParams();
    params.set("channel", channel);
    params.set("limit", String(PAGE_SIZE));
    if (statusFilter) params.set("status", statusFilter);
    if (branchFilter) params.set("branch_id", branchFilter);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (append && cursor) {
      params.set("before_created_at", cursor.created_at);
      params.set("before_id", cursor.id);
    }
    return `/pos/orders?${params.toString()}`;
  }, [channel, statusFilter, branchFilter, dateFrom, dateTo, cursor]);

  const load = useCallback(async ({ append = false } = {}) => {
    setLoading(true);
    try {
      const r = await api.get(buildUrl(append));
      const rows = r.data || [];
      setOrders((prev) => (append && prev ? [...prev, ...rows] : rows));
      setHasMore(rows.length === PAGE_SIZE);
      const last = rows[rows.length - 1];
      setCursor(last ? { created_at: last.created_at, id: last.id } : null);
    } catch (e) {
      toast.error(parseApiError(e, "Lỗi tải đơn").message);
    } finally {
      setLoading(false);
    }
  }, [buildUrl]);

  useEffect(() => {
    setCursor(null);
    load({ append: false });
  }, [channel, statusFilter, branchFilter, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (refreshToken === undefined) return;
    setCursor(null);
    load({ append: false });
  }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = setInterval(() => {
      setCursor(null);
      load({ append: false });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  return (
    <div className="mt-4 bg-white border border-stone-200 rounded-sm" data-testid="pos-recent-orders">
      <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2 flex-wrap">
        <Receipt className="w-4 h-4 text-stone-700" aria-hidden="true" />
        <h3 className="font-serif text-base font-bold mr-auto">Đơn gần đây — {channelLabel}</h3>
        <Select value={statusFilter || "_all"} onValueChange={(v) => setStatusFilter(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-8 w-36 rounded-sm text-xs" aria-label="Lọc trạng thái"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUS_FILTERS.map((o) => <SelectItem key={o.v || "_all"} value={o.v || "_all"}>{o.l}</SelectItem>)}</SelectContent>
        </Select>
        {branches && branches.length > 1 && (
          <Select value={branchFilter || "_all"} onValueChange={(v) => setBranchFilter(v === "_all" ? "" : v)}>
            <SelectTrigger className="h-8 w-40 rounded-sm text-xs" aria-label="Lọc chi nhánh"><SelectValue placeholder="Mọi chi nhánh" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Mọi chi nhánh</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-36 rounded-sm text-xs" aria-label="Từ ngày" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-36 rounded-sm text-xs" aria-label="Đến ngày" />
        <Button type="button" variant="outline" size="sm" onClick={() => load({ append: false })} className="h-8 rounded-sm" aria-label="Làm mới">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
        </Button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-stone-50">
          <tr>
            {["Mã đơn", "Bàn/KH", "SL dòng", "Tổng", "TT", "Thời gian"].map((h) => (
              <th key={h} className="text-left px-4 py-2 text-[10px] uppercase tracking-wider text-stone-700 font-bold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {orders === null && Array.from({ length: 3 }).map((_, i) => (
            <tr key={`sk-${i}`}>
              {Array.from({ length: 6 }).map((__, j) => (
                <td key={j} className="px-4 py-2"><Skeleton className="h-4 w-full" /></td>
              ))}
            </tr>
          ))}
          {orders && orders.map((o) => (
            <tr key={o.id} className="hover:bg-stone-50 cursor-pointer" data-testid={`order-row-${o.code}`} onClick={() => onOpenDetail?.(o.id)}>
              <td className="px-4 py-2 font-mono text-xs">{o.code}</td>
              <td className="px-4 py-2">{o.table_no || o.customer_name || (o.customer_id ? "—" : "—")}</td>
              <td className="px-4 py-2 font-mono text-xs">{o.lines_count ?? "—"}</td>
              <td className="px-4 py-2 font-mono text-sm font-bold">{fmtMoney(o.total)}</td>
              <td className="px-4 py-2">
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-semibold ${statusTone(o.status)}`}>{statusVi(o.status)}</span>
              </td>
              <td className="px-4 py-2 text-xs text-stone-700 font-mono">{fmtDateTimeFixed(o.created_at)}</td>
            </tr>
          ))}
          {orders && orders.length === 0 && (
            <tr><td colSpan={6} className="px-4 py-6 text-center text-stone-700">Chưa có đơn</td></tr>
          )}
        </tbody>
      </table>
      {hasMore && (
        <div className="border-t border-stone-200 p-2 text-center">
          <Button type="button" variant="outline" size="sm" onClick={() => load({ append: true })} disabled={loading} className="rounded-sm">
            {loading ? "Đang tải..." : "Xem thêm"}
          </Button>
        </div>
      )}
    </div>
  );
}
